import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Box } from '@mui/material';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { getAllInvoiceGroups } from 'src/shared/services/invoiceGroupService';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';

export const AdminInvoiceGroupDataGrid = () => {
  const { t } = useTranslation(['invoice']);
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const navigate = useNavigate();
  const isMounted = useIsMounted();

  const [, setLoading] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'owner_id', sort: 'desc' }]);

  const [rows, setRows] = useState<InvoiceGroup[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  // useEffect(() => {
  //   setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  // }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('pageSize', newPageSize);
    scrollIntoView();
  };

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.AdminInvoiceGroupSettings}/${params.row.owner_id}`);
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      setLoading(true);
    }

    try {
      const result = await getAllInvoiceGroups(data.page, data.pageSize, sortModel[0]?.field, sortModel[0]?.sort, null);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        ConsoleHelper.log(result.data.results);
        setRows(
          result.data.results.map((item) => {
            return { ...item, id: item.owner_id };
          }),
        );
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        setLoading(false);
      }
    }
  }, [data.page, data.pageSize, isMounted, sortModel]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('invoice:adminInvoiceGroupOverviewPage.invoiceGroup'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.name}</>;
      },
    },
    {
      field: 'owner_id',
      headerName: t('invoice:adminInvoiceGroupOverviewPage.invoiceGroupNr'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.owner_id}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          py: 1,
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<InvoiceGroup>
        columns={columns}
        rows={rows}
        autoHeight
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          ConsoleHelper.log('newSortModel', newSortModel);

          if (newSortModel.length === 0) {
            const currentSort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
            setSortModel([{ field: sortModel[0].field, sort: currentSort }]);
          } else {
            setSortModel(newSortModel);
          }
        }}
        mode='server'
        onRowClick={onRowClick}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
